import { useCallback, useEffect, useState } from 'react'
import { ShieldCheck, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchRequests,
  createRequest,
  updateStatus,
  deleteRequest,
  KINDS,
  kindLabel,
  statusMeta,
  GD_STR as S,
  type GDPRRequest,
} from './gdprApi'

export function GDPRRequestsScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<GDPRRequest[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [userId, setUserId] = useState('')
  const [ref, setRef] = useState('')
  const [kind, setKind] = useState('access')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRequests(status, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const create = async () => {
    if (!ref.trim() && !userId.trim()) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createRequest({
        subject_user_id: userId.trim() ? Number(userId) : undefined,
        subject_ref: ref.trim(),
        kind,
        reason: reason.trim(),
      })
      setUserId('')
      setRef('')
      setReason('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const setStat = async (id: number, st: string) => {
    let resolution = ''
    if (st === 'completed' || st === 'rejected') {
      resolution = window.prompt(S.resolution) ?? ''
    }
    try {
      await updateStatus(id, st, resolution)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteRequest(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A19.14" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
              placeholder={S.subjectUser}
              className="w-40 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={S.subjectRef}
              className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
              {KINDS.map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={S.reason}
              className="min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button type="button" onClick={create} disabled={busy} className="rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
              {S.add}
            </button>
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="open">open</option>
            <option value="in_progress">in_progress</option>
            <option value="completed">completed</option>
            <option value="rejected">rejected</option>
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => {
                const st = statusMeta(r.status)
                return (
                  <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-primary">{kindLabel(r.kind)}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs ${st.pill}`}>{st.label}</span>
                          {r.overdue && <span className="rounded bg-danger/10 px-1.5 py-0.5 text-xs text-danger">{S.overdue}</span>}
                        </div>
                        <p className="mt-1 text-sm text-secondary">
                          {S.subject}: <span className="text-primary">{r.subject || '—'}</span>
                        </p>
                        {r.reason && <p className="text-xs text-tertiary">{r.reason}</p>}
                        {r.resolution && <p className="text-xs text-tertiary">→ {r.resolution}</p>}
                        <p className="mt-0.5 text-xs text-tertiary">
                          {S.due}: {new Date(r.due_at).toLocaleDateString()}
                          {r.completed_at && ` · ✓ ${new Date(r.completed_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                        {r.status !== 'completed' && r.status !== 'rejected' && (
                          <>
                            {r.status === 'open' && (
                              <button type="button" onClick={() => setStat(r.id, 'in_progress')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                                {S.markInProgress}
                              </button>
                            )}
                            <button type="button" onClick={() => setStat(r.id, 'completed')} className="flex items-center gap-1 rounded-md bg-success px-2 py-1 text-xs text-white">
                              <ShieldCheck size={12} />
                              {S.complete}
                            </button>
                            <button type="button" onClick={() => setStat(r.id, 'rejected')} className="rounded-md border border-border-default px-2 py-1 text-xs text-secondary hover:text-primary">
                              {S.reject}
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
